import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  GitBranch, Plus, Trash2, Play, Copy, CheckCircle2, XCircle,
  Clock, Settings, Eye, RefreshCw, Loader2, Terminal, AlertTriangle
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const PLATFORMS = [
  { value: 'github_actions', label: 'GitHub Actions' },
  { value: 'gitlab_ci', label: 'GitLab CI' },
  { value: 'jenkins', label: 'Jenkins' },
  { value: 'generic', label: 'Generic Webhook' },
];

const SCAN_TYPES = [
  { value: 'web_dast', label: 'Web App DAST' },
  { value: 'mobile_static', label: 'Mobile Static' },
  { value: 'desktop_static', label: 'Desktop Static' },
];

const SEVERITIES = [
  { value: 'critical', label: 'Critical only' },
  { value: 'high', label: 'High and above' },
  { value: 'medium', label: 'Medium and above' },
  { value: 'low', label: 'Low and above' },
];

const PipelineConfigSnippet = ({ pipeline, webhookUrl, snippets }) => {
  const [copied, setCopied] = useState(false);

  const content = snippets ? Object.values(snippets)[0] || '' : '';
  const lang = snippets ? Object.keys(snippets)[0] || 'text' : 'text';

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success('Config copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-gray-500">Webhook URL</Label>
        <div className="flex items-center gap-2 mt-1">
          <code className="flex-1 text-xs bg-gray-900 text-green-400 p-2 rounded font-mono overflow-x-auto" data-testid="webhook-url">
            {webhookUrl}
          </code>
          <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Webhook URL copied'); }}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs text-gray-500">Configuration ({lang})</Label>
          <Button variant="ghost" size="sm" onClick={handleCopy} data-testid="copy-config-btn">
            {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <ScrollArea className="max-h-72">
          <pre className="text-xs bg-gray-900 text-gray-300 p-3 rounded font-mono overflow-x-auto whitespace-pre-wrap">
            {content}
          </pre>
        </ScrollArea>
      </div>
      <div>
        <Label className="text-xs text-gray-500">Webhook Secret</Label>
        <code className="block text-xs bg-gray-100 p-2 rounded font-mono mt-1">{pipeline?.webhook_secret}</code>
      </div>
    </div>
  );
};

export default function CICDPipelinePage() {
  const [pipelines, setPipelines] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [tab, setTab] = useState('pipelines');
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    name: '', platform: 'github_actions', repository_url: '',
    branch_filter: 'main,master,develop', scan_type: 'web_dast',
    auto_scan: true, fail_on_severity: 'critical', notify_on_completion: true,
  });

  const fetchPipelines = useCallback(async () => {
    try {
      const res = await api.get('/cicd/pipelines');
      setPipelines(res.data.pipelines || []);
    } catch (err) {
      console.error('Failed to fetch pipelines', err);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await api.get('/cicd/runs');
      setRuns(res.data.runs || []);
    } catch (err) {
      console.error('Failed to fetch runs', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPipelines(), fetchRuns()]);
      setLoading(false);
    };
    load();
  }, [fetchPipelines, fetchRuns]);

  const handleCreate = async () => {
    if (!form.name) { toast.error('Pipeline name is required'); return; }
    setCreating(true);
    try {
      await api.post('/cicd/pipelines', form);
      toast.success('Pipeline created');
      setShowCreate(false);
      setForm({ name: '', platform: 'github_actions', repository_url: '', branch_filter: 'main,master,develop', scan_type: 'web_dast', auto_scan: true, fail_on_severity: 'critical', notify_on_completion: true });
      fetchPipelines();
    } catch (err) {
      toast.error('Failed to create pipeline');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/cicd/pipelines/${id}`);
      toast.success('Pipeline deleted');
      fetchPipelines();
    } catch (err) {
      toast.error('Failed to delete pipeline');
    }
  };

  const handleTest = async (id) => {
    try {
      await api.post(`/cicd/pipelines/${id}/test`);
      toast.success('Test run triggered');
      fetchRuns();
    } catch (err) {
      toast.error('Failed to trigger test');
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await api.get(`/cicd/pipelines/${id}`);
      setDetailData(res.data);
      setShowDetail(id);
    } catch (err) {
      toast.error('Failed to load pipeline details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cicd-pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GitBranch className="w-6 h-6" /> CI/CD Pipeline Integration
          </h1>
          <p className="text-sm text-gray-500 mt-1">Automate security scanning in your development pipeline</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchPipelines(); fetchRuns(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-pipeline-btn">
            <Plus className="w-4 h-4 mr-1" /> New Pipeline
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pipelines" data-testid="pipelines-tab">Pipelines ({pipelines.length})</TabsTrigger>
          <TabsTrigger value="runs" data-testid="runs-tab">Recent Runs ({runs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pipelines" className="mt-4">
          {pipelines.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <GitBranch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No CI/CD pipelines configured</p>
                <p className="text-sm text-gray-400 mt-1">Create a pipeline to automate security scanning</p>
                <Button className="mt-4" onClick={() => setShowCreate(true)} data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-1" /> Create Pipeline
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pipelines.map((p) => (
                <Card key={p.id} data-testid={`pipeline-card-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${p.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <div>
                          <h3 className="font-semibold">{p.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {PLATFORMS.find(pl => pl.value === p.platform)?.label || p.platform}
                            {p.repository_url && ` - ${p.repository_url}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{p.scan_type?.replace('_', ' ')}</Badge>
                        <Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetail(p.id)} data-testid={`view-pipeline-${p.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleTest(p.id)} data-testid={`test-pipeline-${p.id}`}>
                          <Play className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-pipeline-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-xs text-gray-500">
                      <span>Branches: {p.branch_filter}</span>
                      <span>Fail on: {p.fail_on_severity}</span>
                      <span>Auto-scan: {p.auto_scan ? 'Yes' : 'No'}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          {runs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Terminal className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No pipeline runs yet</p>
                <p className="text-sm text-gray-400 mt-1">Trigger a scan from your CI/CD pipeline or use the test button</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pipeline</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Commit</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Started</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id} data-testid={`run-row-${run.id}`}>
                        <TableCell className="font-medium">{run.pipeline_name || 'Unknown'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs"><GitBranch className="w-3 h-3 mr-1" />{run.branch}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{run.commit_sha?.slice(0, 8) || '-'}</TableCell>
                        <TableCell className="text-xs capitalize">{run.trigger_type}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {run.scan_status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
                            {run.scan_status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />}
                            {run.scan_status === 'failed' && <XCircle className="w-3.5 h-3.5 text-red-500" />}
                            {run.scan_status === 'pending' && <Clock className="w-3.5 h-3.5 text-gray-400" />}
                            <span className="text-xs capitalize">{run.scan_status}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {run.result === 'pass' && <Badge className="bg-green-600 text-xs">Pass</Badge>}
                          {run.result === 'fail' && <Badge className="bg-red-600 text-xs">Fail</Badge>}
                          {run.result === 'pending' && <Badge variant="outline" className="text-xs">Pending</Badge>}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(run.started_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Pipeline Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create CI/CD Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pipeline Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="My App Security Scan" data-testid="pipeline-name-input" />
            </div>
            <div>
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={v => setForm({...form, platform: v})}>
                <SelectTrigger data-testid="platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Repository URL (optional)</Label>
              <Input value={form.repository_url} onChange={e => setForm({...form, repository_url: e.target.value})} placeholder="https://github.com/org/repo" data-testid="repo-url-input" />
            </div>
            <div>
              <Label>Branch Filter (comma-separated)</Label>
              <Input value={form.branch_filter} onChange={e => setForm({...form, branch_filter: e.target.value})} placeholder="main,develop" data-testid="branch-filter-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scan Type</Label>
                <Select value={form.scan_type} onValueChange={v => setForm({...form, scan_type: v})}>
                  <SelectTrigger data-testid="scan-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCAN_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fail Pipeline On</Label>
                <Select value={form.fail_on_severity} onValueChange={v => setForm({...form, fail_on_severity: v})}>
                  <SelectTrigger data-testid="severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Auto-scan on trigger</Label>
              <Switch checked={form.auto_scan} onCheckedChange={v => setForm({...form, auto_scan: v})} data-testid="auto-scan-switch" />
            </div>
            <Button className="w-full" onClick={handleCreate} disabled={creating} data-testid="submit-pipeline-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Create Pipeline
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pipeline Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Pipeline Configuration
            </DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium">{detailData.pipeline?.name}</span></div>
                <div><span className="text-gray-500">Platform:</span> <span className="font-medium">{PLATFORMS.find(p => p.value === detailData.pipeline?.platform)?.label}</span></div>
                <div><span className="text-gray-500">Status:</span> <Badge variant={detailData.pipeline?.status === 'active' ? 'default' : 'secondary'}>{detailData.pipeline?.status}</Badge></div>
                <div><span className="text-gray-500">Scan Type:</span> <span className="font-medium">{detailData.pipeline?.scan_type}</span></div>
                <div><span className="text-gray-500">Branches:</span> <span className="font-medium">{detailData.pipeline?.branch_filter}</span></div>
                <div><span className="text-gray-500">Fail on:</span> <span className="font-medium capitalize">{detailData.pipeline?.fail_on_severity}</span></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2"><Terminal className="w-4 h-4" /> Integration Setup</h4>
                <PipelineConfigSnippet
                  pipeline={detailData.pipeline}
                  webhookUrl={detailData.webhook_url}
                  snippets={detailData.config_snippets}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
